// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useTranslation } from 'react-i18next';
import { useAggregationData } from '../../providers';
import { produce } from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { useAccountsDebt } from '@shared/advisory/aggregation/hooks';
import { useSelector } from 'react-redux';
import { selectClientInstitutionsNames } from '@shared/data-access/slices/clients';
import { type KpiBoxesProps, type KpiPeriod } from '@shared/ui-kit';
import { formatToMoney, formatToPercent } from '@shared/ui-kit/utils';
import {
  buildDebtBreakdownByBank,
  buildDebtBreakdownByType,
} from '@shared/advisory/aggregation/utils/build-debt-breakdown';
import {
  buildLoansTableRows,
  buildGroupedLoansTableRows,
} from '@shared/advisory/aggregation/utils/build-loans-table-rows';
import {
  buildCreditLinesTableRows,
  buildGroupedCreditLinesTableRows,
} from '@shared/advisory/aggregation/utils/build-credit-lines-table-rows';
import {
  buildNextAmortizations,
  hasAmortizationProducts,
} from '@shared/advisory/aggregation/utils/build-next-amortizations';
import { type LoaderStatus } from '../loader';

export const useLoans = () => {
  const { t } = useTranslation();
  const {
    periodPicker,
    currency,
    updatedDate,
    refreshMode,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    clientId,
    onRefresh: onRefreshAggregation,
    shouldRefresh,
  } = useAggregationData();

  const institutionNames = useSelector(selectClientInstitutionsNames);

  const { accountsDebt, hasDebtData, hasDebtChartData, accountsDebtQuery } =
    useAccountsDebt({
      clientId,
      ibans: selectedAccounts,
      institutionId,
      currency,
      start_date: periodPicker.start_date,
      end_date: periodPicker.end_date,
      skip: selectedAccounts.length === 0,
    });

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = accountsDebtQuery.isLoading;
    const isError = accountsDebtQuery.isError;
    const isFetching = accountsDebtQuery.isFetching;

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    accountsDebtQuery.isError,
    accountsDebtQuery.isFetching,
    accountsDebtQuery.isLoading,
  ]);

  const onRefresh = useCallback(() => {
    if (shouldRefresh) {
      accountsDebtQuery.refetch();

      return;
    }

    onRefreshAggregation();
  }, [shouldRefresh, onRefreshAggregation, accountsDebtQuery]);

  const kpis = useMemo<KpiBoxesProps[]>(() => {
    const accounts = accountsDebt?.accounts ?? [];

    const period = periodPicker.defaultHist as KpiPeriod;

    const initialShortTermDebt = accounts.reduce(
      (sum, account) => sum + (account.historical_debt.short_term.at(0) ?? 0),
      0,
    );

    const finalShortTermDebt = accounts.reduce(
      (sum, account) => sum + (account.historical_debt.short_term.at(-1) ?? 0),
      0,
    );

    const initialLongTermDebt = accounts.reduce(
      (sum, account) => sum + (account.historical_debt.long_term.at(0) ?? 0),
      0,
    );

    const finalLongTermDebt = accounts.reduce(
      (sum, account) => sum + (account.historical_debt.long_term.at(-1) ?? 0),
      0,
    );

    const initialTotalDebt = initialShortTermDebt + initialLongTermDebt;

    const finalTotalDebt = finalShortTermDebt + finalLongTermDebt;

    const totalDebtReturn =
      initialTotalDebt !== 0
        ? (finalTotalDebt - initialTotalDebt) / initialTotalDebt
        : 0;

    const longTermDebtReturn =
      initialLongTermDebt !== 0
        ? (finalLongTermDebt - initialLongTermDebt) / initialLongTermDebt
        : 0;

    const shortTermDebtReturn =
      initialShortTermDebt !== 0
        ? (finalShortTermDebt - initialShortTermDebt) / initialShortTermDebt
        : 0;

    const totalDebtReturnInvestment = finalTotalDebt - initialTotalDebt;

    const longTermDebtReturnInvestment = finalLongTermDebt - initialLongTermDebt;

    const shortTermDebtReturnInvestment = finalShortTermDebt - initialShortTermDebt;

    const { availableCredit, totalCreditLimit, totalCreditDisposed } = accounts
      .flatMap(account => account.credit_lines ?? [])
      .reduce(
        (totals, line) =>
          produce(totals, draft => {
            const limit = line.limit_amount ?? 0;
            const disposed = line.disposed ?? 0;
            const available = line.available ?? (limit > 0 ? limit - disposed : 0);

            draft.totalCreditLimit += limit;
            draft.totalCreditDisposed += disposed;
            draft.availableCredit += available;
          }),
        {
          availableCredit: 0,
          totalCreditLimit: 0,
          totalCreditDisposed: 0,
        },
      );

    const creditUtilization =
      totalCreditLimit > 0 ? totalCreditDisposed / totalCreditLimit : 0;

    return [
      {
        text: t('aggregator.loans.total-debt', { ns: 'aggregator' }),
        value: formatToMoney(finalTotalDebt, currency),
        dataTestid: 'kpi-total-debt',
        icon: 'Building',
        popupName: 'kpis.totalDebt',
        return: totalDebtReturn,
        returnInvestment: totalDebtReturnInvestment,
        period,
        currency,
        lowerIsBetter: true,
      },
      {
        text: t('aggregator.loans.long-term-debt', { ns: 'aggregator' }),
        value: formatToMoney(finalLongTermDebt, currency),
        dataTestid: 'kpi-debt-long-term',
        icon: 'ArrowDownUp',
        popupName: 'kpis.longTermDebt',
        return: longTermDebtReturn,
        returnInvestment: longTermDebtReturnInvestment,
        period,
        currency,
        lowerIsBetter: true,
      },
      {
        text: t('aggregator.loans.short-term-debt', { ns: 'aggregator' }),
        value: formatToMoney(finalShortTermDebt, currency),
        dataTestid: 'kpi-debt-short-term',
        icon: 'CreditCard',
        popupName: 'kpis.shortTermDebt',
        return: shortTermDebtReturn,
        returnInvestment: shortTermDebtReturnInvestment,
        period,
        currency,
        lowerIsBetter: true,
      },
      {
        text: t('aggregator.loans.available-credit', { ns: 'aggregator' }),
        value: formatToMoney(availableCredit, currency),
        dataTestid: 'kpi-available-credit',
        icon: 'CreditCard',
        popupName: 'kpis.availableCredit',
        footerBoxText:
          totalCreditLimit > 0
            ? t('aggregator.loans.credit-utilization', {
                ns: 'aggregator',
                percent: formatToPercent(creditUtilization),
              })
            : undefined,
      },
    ];
  }, [accountsDebt, currency, periodPicker.defaultHist, t]);

  const debtBreakdownByBank = useMemo(
    () => buildDebtBreakdownByBank(accountsDebt?.accounts, institutionNames),
    [accountsDebt, institutionNames],
  );

  const debtBreakdownByType = useMemo(
    () => buildDebtBreakdownByType(accountsDebt?.accounts),
    [accountsDebt],
  );

  const loansTableRows = useMemo(
    () => buildLoansTableRows(accountsDebt?.accounts, institutionNames),
    [accountsDebt, institutionNames],
  );

  const creditLinesTableRows = useMemo(
    () => buildCreditLinesTableRows(accountsDebt?.accounts, institutionNames),
    [accountsDebt, institutionNames],
  );

  const loansTableData = useMemo(
    () => buildGroupedLoansTableRows(loansTableRows),
    [loansTableRows],
  );

  const creditLinesTableData = useMemo(
    () => buildGroupedCreditLinesTableRows(creditLinesTableRows),
    [creditLinesTableRows],
  );

  const [amortizationPeriodDays, setAmortizationPeriodDays] = useState(85);

  const onAmortizationPeriodChange = useCallback((days: number) => {
    setAmortizationPeriodDays(days);
  }, []);

  const nextAmortizations = useMemo(
    () =>
      buildNextAmortizations(
        accountsDebt?.accounts,
        amortizationPeriodDays,
        institutionNames,
      ),
    [accountsDebt, amortizationPeriodDays, institutionNames],
  );

  const hasNextAmortizationsSection = useMemo(
    () => hasAmortizationProducts(accountsDebt?.accounts),
    [accountsDebt],
  );

  return {
    t,
    periodPicker,
    currency,
    updatedDate,
    refreshMode,
    status,
    hasDebtData,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    onRefresh,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    kpis,
    debtChartData: accountsDebt,
    hasDebtChartData,
    debtBreakdownByBank,
    debtBreakdownByType,
    loansTableData,
    hasLoansTableData: loansTableRows.length > 0,
    creditLinesTableData,
    hasCreditLinesTableData: creditLinesTableRows.length > 0,
    nextAmortizations,
    hasNextAmortizationsSection,
    onAmortizationPeriodChange,
  };
};
